import ModeBadge from '@/components/console/mode-badge';
import StatusPill from '@/components/console/status-pill';
import PageHeader from '@/components/page-header';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FilesTab from '@/pages/apps/developers/components/files-tab';
import HandoffTab from '@/pages/apps/developers/components/handoff-tab';
import LogsTab, { type WebhookEvent } from '@/pages/apps/developers/components/logs-tab';
import SchemaTab, { type DeveloperSchema } from '@/pages/apps/developers/components/schema-tab';
import SqlTab from '@/pages/apps/developers/components/sql-tab';
import TokensTab from '@/pages/apps/developers/components/tokens-tab';
import type { AppShell, Attachment, Connection, Message, ProviderMode } from '@/types/platform';

interface Props {
  app: AppShell;
  access: 'read' | 'write';
  schema: DeveloperSchema | null;
  database: {
    mode: 'shared' | 'dedicated' | null;
    ref: string | null;
    providerMode: ProviderMode;
  };
  bucket: { name: string | null; providerMode: ProviderMode };
  files: Attachment[];
  tokens: Connection[];
  messages: Message[];
  webhookEvents: WebhookEvent[];
  exportAllowed: boolean;
  claim: { allowed: boolean; requestedAt: string | null };
  canManage: boolean;
}

const TABS = [
  { value: 'schema', label: 'Schema' },
  { value: 'sql', label: 'SQL' },
  { value: 'files', label: 'Files' },
  { value: 'logs', label: 'Logs' },
  { value: 'tokens', label: 'Tokens' },
  { value: 'handoff', label: 'Handoff' },
] as const;

const Show = ({
  app,
  access,
  schema,
  database,
  bucket,
  files,
  tokens,
  messages,
  webhookEvents,
  exportAllowed,
  claim,
  canManage,
}: Props) => {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Developer"
        description="The database, bucket, logs, and tokens behind this app."
        badges={
          <StatusPill tone="info">{access === 'write' ? 'Read and write' : 'Read-only'}</StatusPill>
        }
      />

      <Card>
        <CardContent>
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div className="space-y-1">
              <dt className="text-muted-foreground flex flex-wrap items-center gap-2">
                Database <ModeBadge mode={database.providerMode} />
              </dt>
              <dd>
                {database.mode ? `${database.mode}, ` : 'Not provisioned yet'}
                {database.mode ? (
                  <>
                    ref <code className="text-xs break-all">{database.ref ?? 'none'}</code>
                  </>
                ) : null}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-muted-foreground flex flex-wrap items-center gap-2">
                Bucket <ModeBadge mode={bucket.providerMode} />
              </dt>
              <dd>
                {bucket.name ? (
                  <code className="text-xs break-all">{bucket.name}</code>
                ) : (
                  'Not created yet'
                )}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Tabs defaultValue="schema" className="gap-4">
        {/* Six tabs do not fit a phone at full padding. They are tightened there so
            all six show at 390px, and the row scrolls sideways on anything narrower. */}
        <div className="overflow-x-auto">
          <TabsList>
            {TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="px-1.5 sm:px-3">
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="schema">
          <SchemaTab schema={schema} />
        </TabsContent>
        {/* Kept mounted so a typed statement and its result survive a look at another tab. */}
        <TabsContent value="sql" forceMount className="data-[state=inactive]:hidden">
          <SqlTab
            appId={app.id}
            access={access}
            databaseMode={database.mode}
            canManage={canManage}
          />
        </TabsContent>
        <TabsContent value="files">
          <FilesTab files={files} bucket={bucket} />
        </TabsContent>
        <TabsContent value="logs">
          <LogsTab messages={messages} webhookEvents={webhookEvents} />
        </TabsContent>
        <TabsContent value="tokens">
          <TokensTab tokens={tokens} />
        </TabsContent>
        <TabsContent value="handoff">
          <HandoffTab
            appId={app.id}
            exportAllowed={exportAllowed}
            claim={claim}
            canManage={canManage}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Show;
